import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { isPromoActive, isNewActive, promoExpiryLabel } from "../utils/promo";

// specialPrice (optional): the viewing store's special price for this
// product — shown as base struck + red, winning over the promo display
// (matches the order transaction's precedence).
export default function ProductDetailModal({ product, onClose, onAddToCart, specialPrice = null }) {
  const { profile } = useAuth();
  const { t } = useLang();
  // Per-customer price visibility (backlog #4) — staff always see prices.
  const showPrices = profile?.role === "rep" || profile?.role === "admin" || profile?.showPrices !== false;
  const [boxes, setBoxes] = useState(1);
  const [showPartial, setShowPartial] = useState(false);
  const [partial, setPartial] = useState("0");
  // Has the user deliberately used the +/− stepper? Needed so picking a
  // partial fraction can replace the UNTOUCHED default of 1 box (Brian,
  // 2026-08-20: choosing ½ should mean 0.5, not 1.5) without clobbering a
  // box count the user actually chose.
  const [boxesTouched, setBoxesTouched] = useState(false);
  // Tap the photo → full-image lightbox with zoom (Brian, 2026-08-21).
  // zoom 1 = photo FIT to the screen; buttons scale to 4x. Width is set in
  // PIXELS from the photo's real dimensions — percentage widths inside the
  // scroll container zoomed fine on phones but locked on desktop browsers.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [imgDims, setImgDims] = useState(null); // natural {w, h} once loaded

  function openLightbox() {
    setZoom(1);
    setLightboxOpen(true);
  }

  // Largest width at which the whole photo fits on screen (90% wide, 80%
  // tall, never blown past its real size at zoom 1).
  function fitWidth() {
    const maxW = window.innerWidth * 0.9;
    const maxH = window.innerHeight * 0.8;
    if (!imgDims) return maxW;
    return Math.min(imgDims.w, maxW, maxH * (imgDims.w / imgDims.h));
  }

  function stepBoxes(next) {
    setBoxesTouched(true);
    setBoxes(next);
  }

  function handlePartialChange(value) {
    setPartial(value);
    if (!boxesTouched) {
      // Fraction picked while boxes is still the default → the fraction IS
      // the order (0.5 box). Reverting to "none" restores the default 1.
      setBoxes(parseFloat(value) > 0 ? 0 : 1);
    }
  }

  const wholeStock = Math.floor(product.stockQty);
  const displayStock = Math.ceil(product.stockQty * 100) / 100;
  const totalQty = boxes + parseFloat(partial);
  // No upper cap: in-stock items may be ordered beyond stock (excess is
  // backordered). Out-of-stock items stay blocked via the add button below.
  const backordered = wholeStock > 0 && totalQty > product.stockQty;

  function handleAdd() {
    if (totalQty <= 0) return;
    onAddToCart(product, totalQty);
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        {product.imageUrl
          ? <img src={product.imageUrl} alt={product.name} className="modal-image" onClick={openLightbox} />
          : <div className="no-image modal-image">{t("noPhoto")}</div>}

        {lightboxOpen && (
          <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
            <div className="lightbox-controls" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setZoom(z => Math.max(1, z - 0.5))} title="Zoom out">−</button>
              <button onClick={() => setZoom(z => Math.min(4, z + 0.5))} title="Zoom in">+</button>
              <button onClick={() => setLightboxOpen(false)} title="Close">×</button>
            </div>
            <div className="lightbox-scroll">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="lightbox-img"
                style={{ width: `${Math.round(fitWidth() * zoom)}px` }}
                onLoad={(e) => setImgDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}

        <p className="sku">{t("sku")}: {product.sku}</p>
        <h3>
          {isNewActive(product) && <span className="new-badge">{t("newBadge")}</span>}
          {product.name}
        </h3>
        <p className="brand">{product.brand}</p>
        {showPrices && (
          specialPrice != null ? (
            <p className="price">
              <s className="price-struck">${product.basePrice.toFixed(2)}</s>{" "}
              <span className="promo-price">${specialPrice.toFixed(2)} {t("perBox")}</span>
            </p>
          ) : isPromoActive(product) ? (
            <p className="price">
              <s className="price-struck">${product.basePrice.toFixed(2)}</s>{" "}
              <span className="promo-price">${product.promoPrice.toFixed(2)} {t("perBox")}</span>
            </p>
          ) : (
            <p className="price">${product.basePrice.toFixed(2)} {t("perBox")}</p>
          )
        )}
        {isPromoActive(product) && promoExpiryLabel(product) && (
          <p className="short-date-note">{t("shortDate", promoExpiryLabel(product))}</p>
        )}
        <p className={wholeStock > 0 ? "in-stock" : "out-of-stock"}>
          {wholeStock > 0 ? t("inStock", displayStock) : t("outOfStock")}
        </p>

        <div className="modal-qty-section">
          <label>{t("boxes")}</label>
          <div className="qty-add-row">
            <button className="qty-btn" onClick={() => stepBoxes(Math.max(0, boxes - 1))}>−</button>
            <span className="qty-value">{boxes}</span>
            <button className="qty-btn" onClick={() => stepBoxes(boxes + 1)}>+</button>
          </div>
        </div>

        {!showPartial ? (
          <button className="partial-toggle" onClick={() => setShowPartial(true)}>
            {t("needPartial")}
          </button>
        ) : (
          <div className="modal-partial-section">
            <label>{t("partialLabel")}</label>
            <select value={partial} onChange={(e) => handlePartialChange(e.target.value)}>
              <option value="0">{t("partialNone")}</option>
              <option value="0.25">{t("quarterBox")}</option>
              <option value="0.5">{t("halfBox")}</option>
              <option value="0.75">{t("threeQuarterBox")}</option>
            </select>
          </div>
        )}

        <p className="modal-total">{t("totalBoxes", totalQty, totalQty !== 1)}</p>
        {totalQty > 0 && wholeStock <= 0 ? (
          <p className="backorder-note">{t("oosNote")}</p>
        ) : backordered && (
          <p className="backorder-note">
            {t("backorderNote", displayStock)}
          </p>
        )}

        {/* Out-of-stock stays orderable — becomes a backorder */}
        <button
          className="add-btn modal-add-btn"
          disabled={totalQty <= 0}
          onClick={handleAdd}
        >
          {t("addToOrder")}
        </button>
      </div>
    </div>
  );
}
